import { useEffect, useRef, useCallback, useState } from 'react';
import { CALIB_STEPS } from '../config/constants';
import { BaselineStore } from '../engine/BaselineStore';

interface CalibSensorRefs {
  eyeTracker: {
    startCalibration: () => void;
    endCalibration: () => void;
  } | null;
  voice: {
    startCalibCollection: () => void;
    stopCalibCollection: () => number[];
    setThreshold: (t: number) => void;
  };
  keyboard: {
    startCalibCollection: () => void;
    stopCalibCollection: () => number[];
  };
  stylus: {
    startCalibCollection: () => void;
    stopCalibCollection: () => { pressures: number[]; speeds: number[] };
  };
}

interface HeadPoseSource {
  yaw: number;
  pitch: number;
}

interface BodyPoseSource {
  shoulderAngle: number;
}

/** Live sensor readouts for the calibration overlay */
export interface CalibSensorReadouts {
  headSamples: number;
  headYaw: number;
  headPitch: number;
  keySamples: number;
  keyMedianMs: number;
  stylusSamples: number;
  voiceRms: number;
  voiceSamples: number;
}

export interface UseCalibrationReturn {
  calibStep: number;
  elapsed: number;
  isCalibrating: boolean;
  headStable: boolean;
  totalCalibPoints: number;
  acceptedCalibPoints: number;
  currentCalibPoint: { row: number; col: number } | null;
  sensorReadouts: CalibSensorReadouts;
  handleCalibPointClick: () => void;
  advanceStep: () => void;  // Manual "다음" button
}

/**
 * 5-step calibration orchestrator (no time limit — user manually advances).
 */
export function useCalibration(
  active: boolean,
  baselineStore: React.MutableRefObject<BaselineStore>,
  sensors: CalibSensorRefs,
  headPose: HeadPoseSource,
  bodyPose: BodyPoseSource,
  onComplete: () => void,
): UseCalibrationReturn {
  const [step, setStep] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [acceptedPoints, setAcceptedPoints] = useState(0);
  const [currentPoint, setCurrentPoint] = useState<{ row: number; col: number } | null>(null);
  const [headStable, setHeadStable] = useState(false);
  const [sensorReadouts, setSensorReadouts] = useState<CalibSensorReadouts>({
    headSamples: 0, headYaw: 0, headPitch: 0,
    keySamples: 0, keyMedianMs: 0,
    stylusSamples: 0, voiceRms: 0, voiceSamples: 0,
  });

  const stepStartRef = useRef(0);
  const animRef = useRef(0);
  const headYawsRef = useRef<number[]>([]);
  const headPitchesRef = useRef<number[]>([]);
  const bodyAnglesRef = useRef<number[]>([]);
  const voiceSamplesRef = useRef(0);
  const calibGridRef = useRef<{ row: number; col: number }[]>([]);
  const calibIdxRef = useRef(0);
  const activeRef = useRef(false);
  const stepRef = useRef(0);

  // Keep refs in sync for use in RAF
  const headPoseRef = useRef(headPose);
  headPoseRef.current = headPose;
  const bodyPoseRef = useRef(bodyPose);
  bodyPoseRef.current = bodyPose;

  const generateGrid = useCallback(() => {
    const points: { row: number; col: number }[] = [];
    const n = 3;
    for (let r = 0; r < n; r++) {
      for (let c = 0; c < n; c++) {
        points.push({ row: r, col: c });
      }
    }
    for (let i = points.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [points[i], points[j]] = [points[j], points[i]];
    }
    return points;
  }, []);

  const startStep = useCallback((stepIdx: number) => {
    const stepDef = CALIB_STEPS[stepIdx];
    if (!stepDef) return;

    stepStartRef.current = Date.now();
    stepRef.current = stepIdx;
    setStep(stepIdx);
    setElapsed(0);

    switch (stepDef.sensor) {
      case 'eyetracker':
        calibGridRef.current = generateGrid();
        calibIdxRef.current = 0;
        setAcceptedPoints(0);
        setCurrentPoint(calibGridRef.current[0] || null);
        sensors.eyeTracker?.startCalibration();
        headYawsRef.current = [];
        headPitchesRef.current = [];
        bodyAnglesRef.current = [];
        break;
      case 'head':
        // Continue collecting from eye tracker step (don't reset)
        setHeadStable(false);
        break;
      case 'key':
        sensors.keyboard.startCalibCollection();
        break;
      case 'stylus':
        sensors.stylus.startCalibCollection();
        break;
      case 'voice':
        sensors.voice.startCalibCollection();
        voiceSamplesRef.current = 0;
        break;
      case 'camera':
        // Camera angle check — handled by CalibrationOverlay UI.
        break;
      case 'paper':
        // Paper edge calibration — handled by CalibrationOverlay's click UI.
        break;
      case 'rppg':
        break;
    }
  }, [generateGrid, sensors]);

  const finalizeStep = useCallback((stepIdx: number) => {
    const stepDef = CALIB_STEPS[stepIdx];
    if (!stepDef) return;

    switch (stepDef.sensor) {
      case 'eyetracker':
        sensors.eyeTracker?.endCalibration();
        setCurrentPoint(null);
        break;
      case 'head':
        baselineStore.current.finalizeHead(headYawsRef.current, headPitchesRef.current);
        if (bodyAnglesRef.current.length > 5) {
          baselineStore.current.finalizeBody(bodyAnglesRef.current);
        }
        break;
      case 'key': {
        const intervals = sensors.keyboard.stopCalibCollection();
        baselineStore.current.finalizeKeyboard(intervals);
        break;
      }
      case 'stylus': {
        const { pressures, speeds } = sensors.stylus.stopCalibCollection();
        baselineStore.current.finalizeStylus(pressures, speeds);
        break;
      }
      case 'voice': {
        const rmsValues = sensors.voice.stopCalibCollection();
        baselineStore.current.finalizeVoice(rmsValues);
        sensors.voice.setThreshold(baselineStore.current.voiceThreshold);
        break;
      }
    }
  }, [sensors, baselineStore]);

  // Manual advance to next step
  const advanceStep = useCallback(() => {
    const currentStep = stepRef.current;
    finalizeStep(currentStep);

    const nextStep = currentStep + 1;
    if (nextStep >= CALIB_STEPS.length) {
      activeRef.current = false;
      onComplete();
    } else {
      startStep(nextStep);
    }
  }, [finalizeStep, startStep, onComplete]);

  // Calibration point click (eye tracker step)
  const lastClickRef = useRef(0);
  const handleCalibPointClick = useCallback(() => {
    const stepDef = CALIB_STEPS[stepRef.current];
    if (stepDef?.sensor !== 'eyetracker') return;
    const now = Date.now();
    if (now - lastClickRef.current < 800) return;
    lastClickRef.current = now;

    const nextIdx = calibIdxRef.current + 1;
    setAcceptedPoints(nextIdx);

    if (nextIdx >= calibGridRef.current.length) {
      setCurrentPoint(null);
    } else {
      calibIdxRef.current = nextIdx;
      setCurrentPoint(calibGridRef.current[nextIdx]);
    }
  }, []);

  // Animation loop — data collection + live readout updates (no auto-advance)
  useEffect(() => {
    if (!active) {
      activeRef.current = false;
      return;
    }

    if (!activeRef.current) {
      activeRef.current = true;
      startStep(0);
    }

    let lastReadoutUpdate = 0;

    const tick = () => {
      if (!activeRef.current) return;
      animRef.current = requestAnimationFrame(tick);

      const now = Date.now();
      const elapsedSec = (now - stepStartRef.current) / 1000;
      setElapsed(elapsedSec);

      const currentStepIdx = stepRef.current;
      const stepDef = CALIB_STEPS[currentStepIdx];
      if (!stepDef) return;

      // Collect head/body data during applicable steps (throttle to ~20Hz)
      if (stepDef.sensor === 'eyetracker' || stepDef.sensor === 'head') {
        headYawsRef.current.push(headPoseRef.current.yaw);
        headPitchesRef.current.push(headPoseRef.current.pitch);
        bodyAnglesRef.current.push(bodyPoseRef.current.shoulderAngle);
        // Keep max 600 samples (~10 seconds at 60fps)
        if (headYawsRef.current.length > 600) {
          headYawsRef.current = headYawsRef.current.slice(-600);
          headPitchesRef.current = headPitchesRef.current.slice(-600);
          bodyAnglesRef.current = bodyAnglesRef.current.slice(-600);
        }
      }

      // Voice sample count
      if (stepDef.sensor === 'voice') {
        voiceSamplesRef.current++;
      }

      // Update live readouts + stability detection at ~4Hz
      if (now - lastReadoutUpdate > 250) {
        lastReadoutUpdate = now;

        // Head pose stability: check std dev of last 60 samples (~1 second)
        let stable = false;
        const recentN = 60;
        if (headYawsRef.current.length >= recentN) {
          const recentYaws = headYawsRef.current.slice(-recentN);
          const recentPitches = headPitchesRef.current.slice(-recentN);
          const yawMean = recentYaws.reduce((a, b) => a + b, 0) / recentN;
          const pitchMean = recentPitches.reduce((a, b) => a + b, 0) / recentN;
          const yawStd = Math.sqrt(recentYaws.reduce((s, v) => s + (v - yawMean) ** 2, 0) / recentN);
          const pitchStd = Math.sqrt(recentPitches.reduce((s, v) => s + (v - pitchMean) ** 2, 0) / recentN);
          // Stable if both yaw/pitch std < 2 degrees and enough total samples
          stable = yawStd < 2 && pitchStd < 2 && headYawsRef.current.length >= 120;
        }
        setHeadStable(stable);

        setSensorReadouts({
          headSamples: headYawsRef.current.length,
          headYaw: headPoseRef.current.yaw,
          headPitch: headPoseRef.current.pitch,
          keySamples: 0,
          keyMedianMs: 0,
          stylusSamples: 0,
          voiceRms: 0,
          voiceSamples: voiceSamplesRef.current,
        });
      }
    };

    animRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(animRef.current);
    };
  }, [active, startStep]);

  return {
    calibStep: step,
    elapsed,
    isCalibrating: active,
    headStable,
    totalCalibPoints: 9,
    acceptedCalibPoints: acceptedPoints,
    currentCalibPoint: currentPoint,
    sensorReadouts,
    handleCalibPointClick,
    advanceStep,
  };
}
